import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {Button} from 'primereact/button'
import {Dialog} from 'primereact/dialog'
import {Divider} from 'primereact/divider'
import {InputSwitch} from 'primereact/inputswitch'
import {InputText} from 'primereact/inputtext'
import {MultiSelect} from 'primereact/multiselect'
import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react'
import {v4 as uuidv4} from 'uuid'
import {HTTP_STATUS_OK, SystemRole} from '../../Constants'
import {useAppConfig} from '../../context/app/AppContext'
import {useUserHospital} from '../../context/hospital/UserHospitalContext'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {QueryKeyManager} from '../../query-manager/QueryKeysManager'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {useAuth} from '../authentication/AuthProvider'
import {getClientId} from '../authentication/AuthUtils'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {
    getStaticListEventKey,
    onStaticListTableChangeRemoveListener,
    staticListEvent,
    staticListEventTypes,
} from '../static-list/broadcast/StaticListEvent'
import {useListStaticList} from '../static-list/query-keys/StaticListKeysManager'
import {UserPropertyType} from '../user-property/UserPropertyConstants'
import {
    useAssignPatient,
    useListUserStaticList,
} from '../user-property/query-keys/UserPropertyQueryManager'
import {useDatatableContext} from './DataTableContext'
import {
    assignmentEvent,
    assignmentEventTypes,
    getAssignmentEventKey,
    removeToggleAssignmentFormListener,
} from './broadcast/AssignmentEvent'

const InitialHospitalFilterRequest = {}
const InitialStaticListFilterRequest = {}
const StyledAssignmentStaticList = forwardRef(({userIds = []}, ref) => {
    useSignals()
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))
    //#region Context
    const {user} = useAuth()
    const {userRole} = useAppConfig()
    const {currentHospital} = useUserHospital()
    const {tableType} = useDatatableContext()
    //#endregion

    //#region Translate
    const {translateKeys, translateCommons, translateOptions} = TranslateUtils()
    //#endregion

    //#region Ref
    const multiSelectRef = useRef(null)
    const inputRefs = useRef([])
    //#endregion

    //#region State
    const visible = useSignal(false)
    const panelVisible = useSignal(false)
    const selectedItems = useSignal([])
    const override = useSignal(false)

    const newStaticList = useSignal({
        name: '',
        description: '',
    })

    const handleChangeSelectedItems = e => {
        selectedItems.value = e.value
    }
    //#endregion

    //#region Query
    const queryClient = useQueryClient()

    const staticListOwnedFilterRequest = useMemo(
        () => ({
            hospitalId:
                userRole === SystemRole.USER_WITH_ROLE
                    ? currentHospital?.id
                    : null,
            ownerId: userRole === SystemRole.USER_WITH_ROLE ? user?.id : null,
            userId: userIds && userIds.length === 1 ? userIds[0] : null,
        }),
        [currentHospital?.id, user?.id, userIds, userRole],
    )

    const {data: staticListOptions, refetch: refetchStaticListOptions} =
        useListStaticList(
            user?.id,
            true,
            {
                ...InitialStaticListFilterRequest,
                hospitalId:
                    userRole === SystemRole.USER_WITH_ROLE
                        ? currentHospital?.id
                        : null,
                ownerId: user?.id,
            },
            !!user?.id,
        )

    const {data: ownedStaticList, refetch: refetchOwnedStaticList} =
        useListUserStaticList(
            user?.id,
            false,
            staticListOwnedFilterRequest,
            !!user?.id && userIds && userIds.length === 1,
        )

    const {mutateAsync: assignPatientToStaticList} = useAssignPatient(
        UserPropertyType.userStaticList,
        user?.id,
    )
    //#endregion

    //#region Memo
    const staticListIds = useMemo(() => {
        return staticListOptions?.map(x => x.value)
    }, [staticListOptions])

    const ownStaticListIds = useMemo(() => {
        if (
            !ownedStaticList ||
            ownedStaticList?.length < 1 ||
            (!!userIds && userIds.length > 1)
        ) {
            return []
        }

        const filterOwnedStaticList = ownedStaticList?.map(x => {
            if (!!x.staticListId && staticListIds?.includes(x.staticListId)) {
                return x.staticListId
            }
        })

        const filterOwnedStaticListIds = filterOwnedStaticList?.filter(x => !!x)

        return filterOwnedStaticListIds
    }, [ownedStaticList, staticListIds, userIds])

    //#endregion

    const handleShowForm = () => {
        blockOverflownScroll()
        visible.value = true

        if (ownStaticListIds?.length > 0 && userIds?.length === 1) {
            selectedItems.value = [...ownStaticListIds]
        }
    }

    const handleHideForm = () => {
        unblockOverflownScroll()
        visible.value = false

        selectedItems.value = []
        override.value = false
        newStaticList.value = {
            name: '',
            description: '',
        }
    }

    const handleOnSubmit = async () => {
        if (!userIds || userIds?.length < 1) {
            showNotification(
                severityNotification.warn,
                'Please select the patients you want to assign to the Static List !',
            )

            return
        }

        if (
            !override.value &&
            selectedItems.value?.length < 1 &&
            !newStaticList.value?.name
        ) {
            showNotification(
                severityNotification.warn,
                'Please select the Static List or Create a new list that you want to assign to the selected patients !',
            )
            return
        }

        let toPostData = {
            userIds: userIds ? userIds : [],
            staticListIds: selectedItems.value ? selectedItems.value : [],
            hospitalId:
                userRole === SystemRole.USER_WITH_ROLE
                    ? currentHospital?.id
                    : null,
            name: newStaticList.value?.name || null,
            description: newStaticList.value?.description || null,
            override: userIds?.length > 1 ? override.value : true,
        }

        const responseStatus = await assignPatientToStaticList(toPostData)
        if (responseStatus && responseStatus === HTTP_STATUS_OK) {
            const {userPropertyKeys} = QueryKeyManager(
                QueryKeyTypes.userProperty,
                user?.id,
            )

            queryClient.invalidateQueries({
                queryKey: userPropertyKeys.list({
                    type: UserPropertyType.userStaticList,
                    simple: false,
                    filterRequest: staticListOwnedFilterRequest,
                }),
            })

            const {patientKeys} = QueryKeyManager(
                QueryKeyTypes.patient,
                user?.id,
            )

            queryClient.invalidateQueries({
                queryKey: patientKeys.pattern(),
            })

            handleHideForm()
        }
    }

    //#region Render & Template
    const panelVisibilityHandler = () => ({
        optionLabel: 'label',
        optionValue: 'value',
        emptyFilterMessage: translateCommons('No results found'),
        resetFilterOnHide: true,
        filter: true,
        filterBy: 'label',
        filterPlaceholder: translateCommons('Search here'),
        showClear: true,
        onShow: () => (panelVisible.value = true),
        onHide: () => (panelVisible.value = false),
    })

    const panelVisiblilityProps = panelVisibilityHandler()

    const renderPanelFooterTemplate = selectedData => {
        let shouldRenderItemSelected = false
        let itemSelectedLength = 0

        if (!!selectedData && typeof selectedData === 'object') {
            if (isArrayAndNotEmpty(selectedData)) {
                shouldRenderItemSelected = true

                itemSelectedLength = selectedData?.length
            }

            if (
                !shouldRenderItemSelected &&
                Object.keys(selectedData)?.length > 0
            ) {
                shouldRenderItemSelected = true

                itemSelectedLength = Object.keys(selectedData)?.length
            }
        }

        return (
            <div className="p-3" style={{backgroundColor: '#f9f9f9'}}>
                <b>
                    {shouldRenderItemSelected && itemSelectedLength > 0
                        ? `${itemSelectedLength} ${
                              itemSelectedLength > 1 ? 'items' : 'item'
                          } selected`
                        : `No item selected`}
                </b>
            </div>
        )
    }
    //#endregion

    //#region Event Lister for Toggle
    useEffect(() => {
        // TABLE CHANGE
        staticListEvent.on(
            `${getStaticListEventKey(staticListEventTypes.TABLE_CHANGE)}`,
            data => {
                if (
                    !data ||
                    !data?.id ||
                    !data?.clientId ||
                    data?.clientId !== getClientId()
                ) {
                    return
                }

                if (
                    userRole === SystemRole.USER_WITH_ROLE &&
                    !!data?.hospitalId &&
                    data?.hospitalId !== currentHospital?.id
                ) {
                    return
                }

                if (!data?.ownerId || data?.ownerId !== user?.id) {
                    return
                }

                const {staticListKeys} = QueryKeyManager(
                    QueryKeyTypes.staticList,
                    user?.id,
                )

                queryClient.invalidateQueries({
                    queryKey: staticListKeys.pattern(),
                })

                const {userPropertyKeys} = QueryKeyManager(
                    QueryKeyTypes.userProperty,
                    user?.id,
                )

                queryClient.invalidateQueries({
                    queryKey: userPropertyKeys.pattern({
                        type: UserPropertyType.userStaticList,
                    }),
                })
            },
        )

        assignmentEvent.on(
            `${getAssignmentEventKey(
                tableType,
                assignmentEventTypes.ON_TOGGLE_STATIC_LIST,
            )}`,
            () => {
                handleShowForm()
            },
        )

        return () => {
            removeToggleAssignmentFormListener(
                tableType,
                assignmentEventTypes.ON_TOGGLE_STATIC_LIST,
            )

            onStaticListTableChangeRemoveListener()
        }
    }, [])
    //#endregion

    return (
        <Dialog
            header={
                <h5 className="mb-0 font-bold">
                    <strong>
                        {translateCommons(
                            'Assign Patients to your Static Lists',
                        )}
                    </strong>
                </h5>
            }
            visible={visible.value}
            onHide={handleHideForm}
            blockScroll={true}
            style={{width: '50vw'}}
            breakpoints={{'960px': '75vw', '641px': '100vw'}}>
            <div className="w-full">
                <div className="mb-2">
                    <h4 className="font-bold mb-1 underline-60">
                        {translateKeys('Static List')}
                    </h4>
                </div>
                <p>
                    Select one of the{' '}
                    {<span className="font-bold">available Static Lists</span>}{' '}
                    in the hospital or{' '}
                    {
                        <span className="font-bold">
                            Create a new static list
                        </span>
                    }{' '}
                    for the selected patients{' '}
                    <span className="font-bold">
                        ({userIds?.length} patient(s))
                    </span>
                    .
                </p>
                <div className="col-12 mb-3">
                    {userIds?.length > 1 ? (
                        <div className="flex align-items-center justify-content-between py-2 mb-3">
                            <div>
                                <h6 className="font-bold">
                                    Override Existing Assignment
                                </h6>
                                <p>
                                    {translateCommons(
                                        'This option enables you whether to override the existing assignment or not',
                                    )}
                                </p>
                                <p>
                                    <i className="pi pi-info-circle mr-2"></i>
                                    <span className="font-bold">
                                        {override.value ? (
                                            <span>
                                                With override option enabled,{' '}
                                                <span style={{color: 'red'}}>
                                                    any existing assignment will
                                                    be deleted
                                                </span>{' '}
                                                and the selected patients will
                                                be assigned to the static list
                                                regardless of their existing
                                                assignment.{' '}
                                                <span className="text-darkcyan">
                                                    The new static list won't be
                                                    affected
                                                </span>
                                            </span>
                                        ) : (
                                            <span>
                                                With override option disabled,{' '}
                                                <span className="text-darkcyan">
                                                    any existing assignment will
                                                    be kept
                                                </span>{' '}
                                                and the selected patients will
                                                be assigned to the static list
                                                only if they are not already
                                                assigned to them.{' '}
                                                <span className="text-darkcyan">
                                                    The new static list won't be
                                                    affected
                                                </span>
                                            </span>
                                        )}
                                    </span>
                                </p>
                            </div>
                            <div>
                                <InputSwitch
                                    checked={override.value}
                                    onChange={() => {
                                        override.value = !override.value
                                    }}
                                />
                            </div>
                        </div>
                    ) : null}

                    <div className="flex align-items-center justify-content-between py-2">
                        <h6 className="font-bold">
                            {translateCommons('Existing Static Lists')}
                        </h6>
                    </div>
                    <MultiSelect
                        className="w-full h-3rem p-inputtext p-0 font-bold"
                        options={
                            isArrayAndNotEmpty(staticListOptions)
                                ? translateOptions(staticListOptions)
                                : []
                        }
                        placeholder={translateCommons('Select an option')}
                        defaultValue={[]}
                        maxSelectedLabels={3}
                        display={
                            selectedItems.value?.length < 4 ? 'chip' : 'comma'
                        }
                        value={selectedItems.value || []}
                        onChange={handleChangeSelectedItems}
                        itemTemplate={item => {
                            return (
                                <h6 className="mb-0 font-bold">
                                    <span className="mr-2">
                                        <i className="pi pi-list"></i>
                                    </span>
                                    <span className="text-darkcyan capitalized">
                                        {item?.label}
                                    </span>
                                    {item?.extraData
                                        ? ` - Available ${item?.extraData} patient(s)`
                                        : ' - No patient added yet'}
                                </h6>
                            )
                        }}
                        panelFooterTemplate={() =>
                            renderPanelFooterTemplate(selectedItems.value)
                        }
                        {...panelVisiblilityProps}
                    />
                    <p>
                        You have selected{' '}
                        <span className="text-darkcyan">
                            {selectedItems.value?.length < 1
                                ? 'no'
                                : selectedItems.value?.length}{' '}
                            static list(s)
                        </span>{' '}
                        out of{' '}
                        <span className="text-darkcyan">
                            {staticListOptions?.length} available static list(s)
                        </span>
                    </p>
                    <Divider align="center" layout="horizontal">
                        <span className="p-tag px-3">
                            {translateCommons('OR')}
                        </span>
                    </Divider>
                    <h6 className="font-bold">
                        {translateCommons('Create a new Static List')}
                    </h6>
                    <div className="mb-3">
                        <InputText
                            className={`col-12 h-3rem font-bold`}
                            ref={el => (inputRefs.current['name'] = el)}
                            placeholder={translateCommons(
                                'Input static list name',
                            )}
                            type={'text'}
                            value={newStaticList.value?.name || ''}
                            onChange={e => {
                                newStaticList.value = {
                                    ...newStaticList.value,
                                    name: e.target.value,
                                }
                            }}
                        />
                    </div>
                    <div className="mb-3">
                        <InputText
                            className={`col-12 h-3rem font-bold`}
                            ref={el => (inputRefs.current['description'] = el)}
                            placeholder={translateCommons('Input description')}
                            type={'text'}
                            value={newStaticList.value?.description || ''}
                            onChange={e => {
                                newStaticList.value = {
                                    ...newStaticList.value,
                                    description: e.target.value,
                                }
                            }}
                        />
                    </div>

                    <Divider align="center" layout="horizontal" />
                    <h6 className="font-bold mb-2">
                        {translateCommons('Note')}
                    </h6>
                    <ul>
                        <li className="mb-2">
                            You can assign the selected patients to multiple
                            static lists. If the patient is already assigned to
                            the static list, the action will be ignored.
                        </li>
                        <li className="mb-2">
                            If you want to create a new static list, please
                            input the name and description.
                        </li>
                        <li className="mb-2">
                            If you want to remove the patient from the static
                            list, please select only that patient and change the
                            static list assignment.
                        </li>
                    </ul>
                    {selectedItems.value?.length > 0 ||
                    !!newStaticList.value?.name ? (
                        <React.Fragment>
                            <Divider align="center" layout="horizontal" />
                            <h6 className="font-bold">
                                {translateCommons('Summary')}
                            </h6>
                            <p>
                                {' '}
                                You are about to assign the selected patients to
                                the following Static List:
                            </p>
                            <ul>
                                {selectedItems.value?.length > 0 ? (
                                    <React.Fragment>
                                        {selectedItems.value?.map(
                                            (selectedStaticList, index) => {
                                                if (index > 3) {
                                                    return null
                                                }

                                                const staticList =
                                                    staticListOptions?.find(
                                                        x =>
                                                            x.value ===
                                                            selectedStaticList,
                                                    )

                                                if (!staticList) {
                                                    return null
                                                }

                                                return (
                                                    <li
                                                        key={`${uuidv4()}`}
                                                        className="mb-2">
                                                        <span className="text-darkcyan font-bold capitalized">
                                                            {staticList.label}
                                                        </span>
                                                        {staticList?.extraData
                                                            ? ` - Available ${staticList?.extraData} patient(s)`
                                                            : ' - No patient added yet'}
                                                    </li>
                                                )
                                            },
                                        )}
                                    </React.Fragment>
                                ) : null}

                                {selectedItems.value?.length > 4 && (
                                    <li>
                                        <span className="text-darkcyan font-bold">
                                            {selectedItems.value?.length - 4}{' '}
                                            more existing static lists
                                        </span>
                                    </li>
                                )}

                                {newStaticList.value?.name ? (
                                    <React.Fragment>
                                        <li>
                                            <span className="text-darkcyan font-bold capitalized">
                                                {newStaticList.value?.name}
                                            </span>
                                            - New Input
                                        </li>
                                    </React.Fragment>
                                ) : null}
                            </ul>
                        </React.Fragment>
                    ) : null}
                </div>

                <Button
                    label={translateCommons('SUBMIT')}
                    icon="pi pi-send"
                    onClick={handleOnSubmit}
                    autoFocus
                    className="col-12 h-3rem mb-3"
                    disabled={
                        selectedItems.value?.length < 1 &&
                        !newStaticList.value?.name &&
                        !override.value
                    }
                />
            </div>
        </Dialog>
    )
})

export default StyledAssignmentStaticList
